import React, { useEffect, useState } from 'react';
import api from '../api';

// Antwortseite für Platzangebote. Aufruf aus der E-Mail: /antwort#t=<schlüssel>&w=annehmen|ausschlagen
// Der Schlüssel steht hinter „#" und wird nur im Request-Body übertragen (nie in einer URL/Log).
// Gespeichert wird erst nach einem Klick hier – automatische Link-Prüfer von Mailprogrammen lösen nichts aus.

function ausHash() {
  const p = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const w = p.get('w');
  return { token: p.get('t') || '', wunsch: ['annehmen', 'ausschlagen'].includes(w) ? w : null };
}

// gueltig_bis ist Mitternacht nach dem letzten Antworttag → als „Samstag, 19.09.2026, 23:59" anzeigen
const fmtFrist = (d) => new Date(new Date(d).getTime() - 60000).toLocaleString('de-DE', {
  weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
});
const fmtZeit = (d) => new Date(d).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' });

function Kontakt({ kontakt }) {
  if (!kontakt?.email) return null;
  return (
    <p style={{ fontSize: '0.88rem', color: '#555', marginTop: '1.25rem', lineHeight: 1.6 }}>
      Bei Fragen wenden Sie sich bitte an {kontakt.name ? `${kontakt.name}: ` : ''}
      <a href={`mailto:${kontakt.email}`} style={{ color: 'var(--accent)' }}>{kontakt.email}</a>
    </p>
  );
}

const karte = {
  background: '#fff', border: '1px solid var(--border)', borderRadius: 10,
  padding: '1.25rem', marginBottom: '1rem',
};

export default function PlatzangebotAntwort() {
  const [{ token, wunsch }] = useState(ausHash);
  const [laden, setLaden] = useState(true);
  const [daten, setDaten] = useState(null);
  const [fehler, setFehler] = useState(null); // { text, kontakt }
  const [auswahl, setAuswahl] = useState(null);
  const [senden, setSenden] = useState(false);
  const [sendeFehler, setSendeFehler] = useState('');

  useEffect(() => {
    if (!token) {
      setFehler({ text: 'Dieser Link ist unvollständig. Bitte öffnen Sie ihn direkt aus der E-Mail.' });
      setLaden(false);
      return;
    }
    api.post('/api/platzangebot/abfrage', { token })
      .then(r => {
        setDaten(r.data);
        if (r.data.zustand === 'offen' && wunsch) setAuswahl(wunsch === 'annehmen' ? 'angenommen' : 'ausgeschlagen');
      })
      .catch(err => setFehler({
        text: err.response?.data?.error || 'Die Anfrage konnte nicht geladen werden. Bitte versuchen Sie es später erneut.',
        kontakt: err.response?.data?.kontakt,
      }))
      .finally(() => setLaden(false));
  }, [token, wunsch]);

  const bestaetigen = async () => {
    setSenden(true);
    setSendeFehler('');
    try {
      const r = await api.post('/api/platzangebot/antwort', { token, antwort: auswahl });
      setDaten(r.data);
    } catch (err) {
      if (err.response?.status === 409 && err.response.data?.zustand) {
        setDaten(err.response.data); // bereits beantwortet / abgelaufen – aktuellen Stand zeigen
      } else {
        setSendeFehler(err.response?.data?.error || 'Die Antwort konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.');
      }
    } finally {
      setSenden(false);
    }
  };

  if (laden) {
    return <div className="form-container"><div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>Lädt…</div></div>;
  }

  if (fehler) {
    return (
      <div className="form-container">
        <h2 className="form-title">Platzangebot</h2>
        <div className="alert alert-error">{fehler.text}</div>
        <Kontakt kontakt={fehler.kontakt} />
      </div>
    );
  }

  const { zustand, kind, ausbildung } = daten;

  // ── Bereits entschieden oder nicht mehr gültig ──
  if (zustand !== 'offen') {
    const angenommen = zustand === 'angenommen';
    const beantwortet = angenommen || zustand === 'ausgeschlagen';
    return (
      <div className="form-container">
        <h2 className="form-title">Platzangebot</h2>
        <div style={{ ...karte, textAlign: 'center' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>{beantwortet ? (angenommen ? '✅' : '✔') : 'ℹ️'}</div>
          {beantwortet ? (
            <>
              <p style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.5rem' }}>
                Der Platz für {kind} ({ausbildung}) wurde {angenommen ? 'angenommen' : 'ausgeschlagen'}.
              </p>
              <p style={{ color: '#555' }}>
                Die Antwort ist am {fmtZeit(daten.beantwortet_am)} Uhr bei uns eingegangen.
                {angenommen ? ' Wir melden uns mit allen weiteren Informationen.' : ' Vielen Dank für Ihre Rückmeldung.'}
              </p>
              <p style={{ color: '#888', fontSize: '0.85rem', marginTop: '0.75rem' }}>
                Eine Antwort ist nur einmal möglich und kann hier nicht mehr geändert werden.
              </p>
              {angenommen && daten.kontakt?.aufnahmeantrag_url && (
                <div style={{ textAlign: 'left', marginTop: '1.25rem', background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 8, padding: '1rem' }}>
                  <p style={{ fontWeight: 700, marginBottom: '0.4rem' }}>Nächster Schritt: Aufnahmeantrag</p>
                  <p style={{ color: '#555', fontSize: '0.9rem', lineHeight: 1.55, marginBottom: '0.75rem' }}>
                    Bitte drucken Sie den Aufnahmeantrag aus, füllen Sie ihn aus und unterschreiben Sie ihn.
                    Geben Sie ihn <strong>spätestens beim zweiten Training</strong> bei der Trainerin oder dem Trainer ab
                    {daten.kontakt.email ? <> oder senden Sie ihn eingescannt an <a href={`mailto:${daten.kontakt.email}`} style={{ color: 'var(--accent)' }}>{daten.kontakt.email}</a></> : ''}.
                  </p>
                  <a href={daten.kontakt.aufnahmeantrag_url} target="_blank" rel="noopener noreferrer"
                    className="submit-btn" style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}>
                    📄 Aufnahmeantrag (PDF) öffnen
                  </a>
                </div>
              )}
            </>
          ) : zustand === 'abgelaufen' ? (
            <p style={{ color: '#555' }}>
              Die Antwortfrist für dieses Platzangebot ist leider abgelaufen. Bitte wenden Sie sich direkt an uns.
            </p>
          ) : (
            <p style={{ color: '#555' }}>Dieses Platzangebot ist nicht mehr gültig. Bitte wenden Sie sich direkt an uns.</p>
          )}
        </div>
        <Kontakt kontakt={daten.kontakt} />
      </div>
    );
  }

  // ── Offen: Auswahl und Bestätigung ──
  return (
    <div className="form-container">
      <h2 className="form-title">Platzangebot</h2>

      <div style={karte}>
        <p style={{ fontSize: '1.05rem', marginBottom: '0.5rem' }}>
          Platz für <strong>{kind}</strong> in der Ausbildung <strong>{ausbildung}</strong>
        </p>
        <p style={{ color: '#555', fontSize: '0.9rem' }}>
          Bitte antworten Sie bis <strong>{fmtFrist(daten.gueltig_bis)} Uhr</strong>.
        </p>
      </div>

      {sendeFehler && <div className="alert alert-error">{sendeFehler}</div>}

      {!auswahl ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <button className="submit-btn" style={{ background: '#2e7d32' }} onClick={() => setAuswahl('angenommen')}>
            ✓ Platz annehmen
          </button>
          <button className="submit-btn" style={{ background: '#fff', color: '#c62828', border: '2px solid #c62828' }}
            onClick={() => setAuswahl('ausgeschlagen')}>
            ✕ Platz ausschlagen
          </button>
        </div>
      ) : (
        <div style={{ ...karte, borderColor: auswahl === 'angenommen' ? '#a5d6a7' : '#ef9a9a',
          background: auswahl === 'angenommen' ? '#f1f8f1' : '#fff5f5' }}>
          <p style={{ fontWeight: 700, fontSize: '1.05rem', marginBottom: '0.5rem' }}>
            {auswahl === 'angenommen'
              ? `Möchten Sie den Platz für ${kind} verbindlich annehmen?`
              : `Möchten Sie den Platz für ${kind} wirklich ausschlagen?`}
          </p>
          <p style={{ color: '#555', fontSize: '0.9rem', marginBottom: '1rem' }}>
            {auswahl === 'ausgeschlagen' && 'Der Platz wird dann an das nächste Kind auf der Warteliste vergeben. '}
            Diese Antwort ist nur einmal möglich und kann danach nicht mehr geändert werden.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            <button className="submit-btn" disabled={senden} onClick={bestaetigen}
              style={{ background: auswahl === 'angenommen' ? '#2e7d32' : '#c62828' }}>
              {senden ? 'Wird gespeichert…' : auswahl === 'angenommen' ? 'Ja, Platz verbindlich annehmen' : 'Ja, Platz ausschlagen'}
            </button>
            <button className="submit-btn" disabled={senden} onClick={() => setAuswahl(null)}
              style={{ background: '#fff', color: '#555', border: '1px solid #ccc' }}>
              Zurück
            </button>
          </div>
        </div>
      )}

      <Kontakt kontakt={daten.kontakt} />
    </div>
  );
}
